// The big tappable tile: an icon over its label (a grid of quick actions), or, `wide`, the icon,
// the text and a chevron in one row (a menu of destinations).
import "./config";
import { Icon, type IconName } from "./Icon";
import { Column, Fill } from "./layout";
import { Pressable } from "./controls";
import { Caption, Body } from "./text";
import { styled } from "tamagui";

const TileFrame = styled(Pressable, {
  name: "Tile",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "4",
  minHeight: 104,
  paddingVertical: "4",
  paddingHorizontal: "3",
  borderRadius: "3",
  borderWidth: "var(--borderW)",
  borderColor: "line",
  backgroundColor: { default: "surface", press: "surface2" },
  textAlign: "left",
  variants: {
    wide: {
      true: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        minHeight: 72,
        width: "100%",
      },
    },
  } as const,
});

export interface TileProps extends Omit<React.ComponentProps<typeof TileFrame>, "wide" | "label"> {
  icon: IconName;
  label: string;
  /** A fainter line under the label. */
  hint?: string | undefined;
  /** The hint's own test id, for a spec that reads the line without the label. */
  hintTestId?: string | undefined;
  wide?: boolean;
}

export function Tile({
  icon,
  label,
  hint,
  hintTestId,
  wide = false,
  ...rest
}: TileProps): React.JSX.Element {
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <TileFrame wide={wide} {...rest}>
      <Icon name={icon} size={wide ? 24 : 22} tone="accent" />
      <Fill flex={wide ? 1 : 0}>
        <Column gap={2}>
          <Body size={wide ? "body" : "small"} weight="strong">
            {label}
          </Body>
          {hint === undefined ? null : <Caption data-testid={hintTestId}>{hint}</Caption>}
        </Column>
      </Fill>
      {wide ? <Icon name="chevron-right" size={18} tone="textFaint" /> : null}
    </TileFrame>
  );
}
